'use client';

import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import type {
  ClaimTypeWithSections,
  FaqWithClaimSectionAndOffice,
  Office,
} from '@jlg-website/database/types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Pencil, Loader2 } from 'lucide-react';

const loading = () => <>Loading...</>;

const FaqFullForm = dynamic(
  () => import('@/components/admin/faq/faq-full-form').then((mod) => mod.FaqFullForm),
  { loading }
);

type FaqFormData = {
  claimsTypesWithSections: ClaimTypeWithSections[];
  offices: Office[];
};

export function FaqEditDialogForm({ value, onEdit }: { value: string; onEdit: () => void }) {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<FaqFormData | null>(null);
  const [defaultValues, setDefaultValues] = useState<FaqWithClaimSectionAndOffice | null>(null);

  const handleSave = () => {
    setOpen(false);
    onEdit();
  };

  useEffect(() => {
    if (!open) return;

    setFormData(null);
    setDefaultValues(null);

    const fetchData = async () => {
      const [data, faq] = await Promise.all([
        fetch('/admin/api/faq/form-data', { cache: 'no-store' }).then((r) => r.json()),
        fetch(`/admin/api/faq/${value}`, { cache: 'no-store' }).then((r) => r.json()),
      ]);
      setFormData(data);
      setDefaultValues(faq);
    };

    fetchData();
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) {
          setFormData(null);
          setDefaultValues(null);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" className="w-full" aria-label="Edit">
          <Pencil /> <strong>Edit FAQ</strong>
        </Button>
      </DialogTrigger>
      <DialogContent className="lg:min-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit FAQ</DialogTitle>
          {!formData || !defaultValues ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="size-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <FaqFullForm
              claimsTypesWithSections={formData.claimsTypesWithSections}
              offices={formData.offices}
              defaultValues={defaultValues}
              onSave={handleSave}
              customCancelButton={
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
              }
            />
          )}
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
}

export default FaqEditDialogForm;
